/**
 * Sets the title and icon for the tab of this component.
 *
 * @private
 */

import memoize from 'memoize-one';
import {AnyAction} from 'redux';
import {enumerate} from 'ts-transformer-enumerate';

import {__} from '~/shared/src/utilities/messageCatalog';
import messageUtils from '~/shared/src/utilities/messageUtils';

import metaModel from '~/shared/src/utilities/metaModel';

import {
    getItemKeyDisplayName,
    getItemNameDisplayName,
    getItemTypeDisplayName,
    getStatusColorStyleString,
    getStatusDisplayName
} from '~/frontend/src/utilities/renderHelpers';

import {
    AppDispatch,
    store
} from '~/frontend/src/store';
import setPanelInfoAction from '~/frontend/src/actions/panelManager/setPanelInfo';
import setPanelIconAction from '~/frontend/src/actions/panelManager/setPanelIcon';
import setPanelTitleAction from '~/frontend/src/actions/panelManager/setPanelTitle';


export type ItemTypeIconConfig = {
    type : 'byItemType';
    schema : Schema;
    itemType : number | string;
} | {
    type : 'byItemId';
    dataContext : string;
    id : number;
};

export type ItemTitleConfig = {
    dataContext : string;
    id : number;
};

const schemata = enumerate<YnSchema>();
const itemTypeIconClassRegExp = new RegExp( `^itemTypeIcon:(${schemata.join( '|' )}):([a-zA-Z_]+)$` );


export const setPanelInfo = ( panelKey : string, icon? : string, title? : string, tooltipTitle? : string, dirty? : boolean, invalid? : boolean, path? : string ) => {
    let tooltip : string | undefined = undefined;

    if ( !!tooltipTitle || ( dirty ?? false ) || ( invalid ?? false ) ) {

        // title
        if ( tooltipTitle ) {
            tooltip = `<div className="yn-tooltip-header">${__( tooltipTitle )}</div>`;
        }
        else if ( title ) {
            tooltip = `<div className="yn-tooltip-header">${__( title )}</div>`;
        }
        else {
            tooltip = '';
        }

        // error
        if ( invalid ) {
            tooltip += `<div className="yn-tooltip-info mb-0">
                            <div className="yn-tooltip-info-title"><span className="fas fa-exclamation-circle yn-error mr-2"></span> ${__( 'YN.base.panel.tooltip.hasError' )}</div>
                        </div>`;
        }

        // dirty
        else if ( dirty ) {
            tooltip += `<div className="yn-tooltip-info mb-0">
                            <div className="yn-tooltip-info-title"><span className="fas fa-exclamation-triangle yn-dirty mr-2"></span> ${__( 'YN.base.panel.tooltip.isDirty' )}</div>
                        </div>`;
        }
    }

    ( store.dispatch as AppDispatch )( setPanelInfoAction( panelKey, { title, icon, tooltip, dirty, invalid, path } ) );
}

export const resolveIconClass = ( iconClass : string ) => {
    const parts = iconClass.match( itemTypeIconClassRegExp );

    let retVal = iconClass;

    if ( parts?.length === 3 ) {
        const itemType = metaModel[ parts[ 1 ] as Schema ].getItemType( parts[ 2 ] );

        if ( itemType?.icon ) {
            retVal = itemType.icon;
        }
    }

    return retVal;
}

export const getPanelIcon = ( defaultIconClass : string, iconClass? : string, itemTypeIconConfig? : ItemTypeIconConfig, additionalIconClass? : string ) : string => {
    let retVal : string = '';

    if ( iconClass ) {
        retVal = resolveIconClass( iconClass );
    }
    else if ( itemTypeIconConfig ) {
        if ( itemTypeIconConfig.type === 'byItemId' ) {
            const item = global.data[ itemTypeIconConfig.dataContext ]?.items[ itemTypeIconConfig.id ];

            if ( item?.itemType.icon ) {
                retVal = item.itemType.icon;
            }
        }
        else {
            const itemType = metaModel[ itemTypeIconConfig.schema ].getItemType( itemTypeIconConfig.itemType );

            if ( itemType?.icon ) {
                retVal = itemType.icon;
            }
        }
    }

    if ( !retVal ) {
        retVal = resolveIconClass( defaultIconClass );
    }

    if ( additionalIconClass ) {
        retVal = `${additionalIconClass} ${retVal}`;
    }

    if ( retVal.indexOf( 'yn-tab-image-icon' ) === -1 ) {
        retVal = `yn-tab-image-icon ${retVal}`;
    }

    return retVal;
}

export const setPanelIcon = ( panelKey : string, defaultIconClass : string, iconClass? : string, itemTypeIconConfig? : ItemTypeIconConfig, additionalIconClass? : string ) => {
    ( store.dispatch as AppDispatch )( setPanelIconAction( panelKey, getPanelIcon( defaultIconClass, iconClass, itemTypeIconConfig, additionalIconClass ) ) );
}

export const getPanelTitle = ( defaultPanelTitle : string, panelTitle? : string, itemTitleConfig? : ItemTitleConfig ) : string => {
    let retVal : string = '';

    if ( panelTitle ) {
        retVal = __( panelTitle );
    }
    else if ( itemTitleConfig ) {
        const item = global.data[ itemTitleConfig.dataContext ]?.items[ itemTitleConfig.id ];

        if ( item?.displayAttributeValue ) {
            retVal = item.displayAttributeValue;
        }
    }

    if ( !retVal ) {
        retVal = __( defaultPanelTitle );
    }

    return retVal;
}

export const setPanelTitle = ( panelKey : string, defaultPanelTitle : string, panelTitle? : string, itemTitleConfig? : ItemTitleConfig ) => {
    ( store.dispatch as AppDispatch )( setPanelTitleAction( panelKey, getPanelTitle( defaultPanelTitle, panelTitle, itemTitleConfig ) ) );
}
